import type { ChatComposerScopeChip } from '@verantos-dev/feature-epi-chat';

import type { Scope } from '../types';
import { useCurrentScope } from './useCurrentScope';

export interface ChatScopeBindings {
  /** The URL-derived current scope. */
  scope: Scope;
  /**
   * Composer-ready scope chip.
   *
   * OR-6021: always `null` now. Previously the chip was rendered from
   * the URL — implying the chat was bound to whatever the user
   * happened to be looking at. That conflated "user is viewing X" with
   * "agent is working on X" and gave a misleading "scope" indicator.
   * The chat already surfaces what the agent is actually working on
   * via artifact cards and tool-call output, and the new `user_context`
   * signal carries "what the user is viewing" as a soft hint to the
   * agent. The slot is preserved on the composer so a future
   * task-scope chip (if product wants one) can mount here without a
   * re-plumb.
   */
  scopeChip: ChatComposerScopeChip | null;
  /**
   * Composer placeholder.
   *
   * OR-6021: kept generic — no per-artifact "Ask about <name>…" string.
   * The agent receives the URL-derived artifact via `user_context` and
   * disambiguates implicit references on its own; the placeholder
   * shouldn't double-advertise the on-screen artifact.
   */
  composerPlaceholder: string | undefined;
}

/**
 * Bridge between the URL-derived ``useCurrentScope`` and the chat
 * composer. Today this is a thin pass-through that emits null for the
 * chip and undefined for the placeholder (composer falls back to its
 * default). Kept as a hook (rather than inlined) so future composer-
 * facing presentation derived from scope has a single landing spot.
 */
export const useChatScopeBindings = (projectId: string): ChatScopeBindings => {
  const scope = useCurrentScope(projectId);
  return { scope, scopeChip: null, composerPlaceholder: undefined };
};
